import type { ResponsePayload } from '../types';

/**
 * Convert loose "text list" values into a trimmed string array.
 * Non-string entries are coerced to strings and empty values are preserved as empty strings.
 */
export const sanitizeTextList = (input?: unknown): string[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input.map((item) => {
    if (typeof item === 'string') {
      return item.trim();
    }

    if (item == null) {
      return '';
    }

    return String(item).trim();
  });
};

/**
 * Prepare data for stable JSON stringification by recursively sorting plain-object keys.
 * Arrays keep their order; non-plain objects are returned unchanged.
 */
export const sortForStableStringify = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => sortForStableStringify(item));
  }

  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => [key, sortForStableStringify((value as Record<string, unknown>)[key])]);

    return Object.fromEntries(entries);
  }

  return value;
};

/**
 * Generate a deterministic signature for response metadata.
 * Returns an empty string when metadata is undefined and "null" when explicitly null.
 */
export const normalizeMetadataSignature = (metadata?: ResponsePayload['metadata']): string => {
  if (typeof metadata === 'undefined') {
    return '';
  }

  if (metadata === null) {
    return 'null';
  }

  try {
    return JSON.stringify(sortForStableStringify(metadata));
  } catch (error) {
    console.warn('Failed to normalize metadata for comparison', error);
    try {
      return JSON.stringify(metadata);
    } catch {
      return '';
    }
  }
};

/**
 * Defensive clone of metadata objects pulled from the API or UI.
 * Returns undefined for unsupported shapes so payloads remain clean.
 */
export const cloneMetadata = (
  metadata: unknown,
): ResponsePayload['metadata'] | undefined => {
  if (typeof metadata === 'undefined') {
    return undefined;
  }

  if (metadata === null) {
    return null;
  }

  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    try {
      return JSON.parse(JSON.stringify(metadata)) as Record<string, unknown>;
    } catch {
      return { ...(metadata as Record<string, unknown>) };
    }
  }

  return undefined;
};

/**
 * Compare two arrays for shallow equality without mutating the inputs.
 */
export const arrayEquals = <T>(a: T[] = [], b: T[] = []): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return false;
    }
  }

  return true;
};

/**
 * Return a sorted copy of a numeric array, or an empty array when the input is not an array.
 */
export const ensureSorted = (values?: number[]): number[] =>
  Array.isArray(values) ? [...values].sort((left, right) => left - right) : [];

type SelectedOptionDetail = {
  id?: number;
  response_order?: number;
};

const normalizeSelectedOptionDetails = (
  details: SelectedOptionDetail[] | undefined,
): number[] => {
  if (!Array.isArray(details)) {
    return [];
  }

  return details
    .map((detail, index) => ({
      ...detail,
      __fallbackIndex: index,
    }))
    .sort((left, right) => {
      const leftHasOrder = typeof left.response_order === 'number';
      const rightHasOrder = typeof right.response_order === 'number';

      if (leftHasOrder && rightHasOrder) {
        return (left.response_order ?? 0) - (right.response_order ?? 0);
      }

      if (leftHasOrder) {
        return -1;
      }

      if (rightHasOrder) {
        return 1;
      }

      return (left.__fallbackIndex ?? 0) - (right.__fallbackIndex ?? 0);
    })
    .map((detail) => (typeof detail.id === 'number' ? detail.id : null))
    .filter((id): id is number => typeof id === 'number');
};

export type NormalizedResponseDraft = {
  question: number;
  rating_value: number | null;
  text_value: string;
  boolean_value: boolean | null;
  selected_options: number[];
  revealed_options: number[];
  custom_other_value: string;
  text_list: string[];
  metadata_signature: string;
};

export const normalizeResponseDraft = (
  questionId: number | null,
  payload: ResponsePayload | undefined,
  questionType?: string,
): NormalizedResponseDraft => {
  const normalizedQuestionId = payload?.question ?? (typeof questionId === 'number' ? questionId : -1);
  const selectedFromPayload = Array.isArray(payload?.selected_options)
    ? [...(payload?.selected_options ?? [])]
    : [];

  const selectedFromDetails = normalizeSelectedOptionDetails(
    (payload as { selected_option_details?: SelectedOptionDetail[] } | undefined)?.selected_option_details,
  );

  const selectedRaw =
    questionType === 'drag_and_drop_ranking' && selectedFromDetails.length
      ? selectedFromDetails
      : selectedFromPayload;

  const selected =
    questionType === 'drag_and_drop_ranking'
      ? selectedRaw
      : ensureSorted(selectedRaw);

  const revealed = ensureSorted(payload?.revealed_options);

  return {
    question: normalizedQuestionId,
    rating_value: typeof payload?.rating_value === 'number' ? payload.rating_value : null,
    text_value: payload?.text_value ?? '',
    boolean_value: typeof payload?.boolean_value === 'boolean' ? payload.boolean_value : null,
    selected_options: selected,
    revealed_options: revealed,
    custom_other_value: payload?.custom_other_value ?? '',
    text_list: sanitizeTextList(payload?.text_list),
    metadata_signature: normalizeMetadataSignature(payload?.metadata),
  };
};
